import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { IndexedDBService } from '../indexeddb.service';
import { PreviewService } from '../preview.service';
import { QuoteConfigService } from './quote-config.service';
import { TagReplacement } from '../../interfaces/ai-content.interface';

export interface SectionCompletionStates {
  isHomeCompleted: boolean;
  isThemeCompleted: boolean;
  isCapabilitiesCompleted: boolean;
  isAboutCompleted: boolean;
  isContactCompleted: boolean;
  isLogoCompleted: boolean;
}

export interface FormBuilderV2Data {
  home: {
    heroTitle: string;
    heroSubtitle: string;
    heroDescription: string;
  };
  template: number;
  theme: string;
  customThemeColor: string;
  capabilities: string[];
  additionalCapabilities: string;
  equipment: string;
  aboutUs: string;
  contact: {
    email: string;
    phone: string;
    streetAddress: string;
    city: string;
    state: string;
    zipCode: string;
  };
  logo: File | null;
  images: {
    aboutUs: File[];
    capabilities: File[];
  };
  currentActiveSection?: string | null; // Persist which section is currently open
  sectionCompletionStates?: SectionCompletionStates; // Persist section completion states
  isAIContentGenerated?: boolean; // Persist AI content generation state
  isAboutGenerated?: boolean; // Persist About Us content generation state
  isCapabilitiesGenerated?: boolean; // Persist Capabilities content generation state
  isDeploying?: boolean; // Persist deployment state
  completionPercentage?: number; // Persist completion percentage
  deploymentId?: string | null; // Persist deployment ID
}

@Injectable({
  providedIn: 'root'
})
export class FormBuilderV2Service {
  private apiUrl = environment.fastApiBaseUrl;
  private formDataSubject = new BehaviorSubject<FormBuilderV2Data>(this.getInitialFormData());
  public formData$ = this.formDataSubject.asObservable();
  private companyGuid: string = '';

  // V1-compatible streams for WebsitePreviewComponent
  private selectedTemplateSubject = new BehaviorSubject<number>(1);
  public selectedTemplate$ = this.selectedTemplateSubject.asObservable();

  private selectedColorThemeSubject = new BehaviorSubject<string>('default');
  public selectedColorTheme$ = this.selectedColorThemeSubject.asObservable();

  // AI Content Generation State Management
  private isAIContentGeneratedSubject = new BehaviorSubject<boolean>(false);
  public isAIContentGenerated$ = this.isAIContentGeneratedSubject.asObservable();

  constructor(
    private http: HttpClient,
    private indexedDBService: IndexedDBService,
    private previewService: PreviewService,
    private quoteConfigService: QuoteConfigService
  ) {}

  // AI Content Generation State Methods
  async setAIContentGenerated(generated: boolean): Promise<void> {
    this.isAIContentGeneratedSubject.next(generated);
    
    // Update form data and persist to IndexedDB
    const currentData = this.formDataSubject.value;
    const updatedData = { ...currentData, isAIContentGenerated: generated };
    this.formDataSubject.next(updatedData);
    await this.saveToIndexedDB(updatedData);
    
    console.log('FormBuilderV2Service: AI content generated state set to:', generated);
  }

  hasAIContentBeenGenerated(): boolean {
    // Check both the BehaviorSubject and the form data for consistency
    const formDataValue = this.formDataSubject.value.isAIContentGenerated || false;
    const subjectValue = this.isAIContentGeneratedSubject.value;
    
    // Return the form data value as the source of truth (persisted in IndexedDB)
    return formDataValue;
  }

  // Content Generation State Methods
  async updateContentGeneration(section: 'about' | 'capabilities', isGenerated: boolean): Promise<void> {
    const currentData = this.formDataSubject.value;
    const updatedData = { ...currentData };
    
    if (section === 'about') {
      updatedData.isAboutGenerated = isGenerated;
    } else if (section === 'capabilities') {
      updatedData.isCapabilitiesGenerated = isGenerated;
    }
    
    this.formDataSubject.next(updatedData);
    await this.saveToIndexedDB(updatedData);
    
    console.log(`FormBuilderV2Service: ${section} content generation state set to:`, isGenerated);
  }

  hasAboutContentBeenGenerated(): boolean {
    return this.formDataSubject.value.isAboutGenerated || false;
  }

  hasCapabilitiesContentBeenGenerated(): boolean {
    return this.formDataSubject.value.isCapabilitiesGenerated || false;
  }

  private getInitialFormData(): FormBuilderV2Data {
    return {
      home: {
        heroTitle: '',
        heroSubtitle: '',
        heroDescription: ''
      },
      template: 1,
      theme: 'default', // Use theme key instead of hex color
      customThemeColor: '#2e58d6',
      capabilities: [],
      additionalCapabilities: '',
      equipment: '',
      aboutUs: '',
      contact: {
        email: '',
        phone: '',
        streetAddress: '',
        city: '',
        state: '',
        zipCode: ''
      },
      logo: null,
      images: {
        aboutUs: [],
        capabilities: []
      },
      currentActiveSection: null, // Default to no section open
      sectionCompletionStates: {
        isHomeCompleted: false,
        isThemeCompleted: false,
        isCapabilitiesCompleted: false,
        isAboutCompleted: false,
        isContactCompleted: false,
        isLogoCompleted: false
      },
      isAIContentGenerated: false, // Default to false for new form data
      isAboutGenerated: false, // Default to false for new form data
      isCapabilitiesGenerated: false, // Default to false for new form data
      isDeploying: false, // Default to false for new form data
      completionPercentage: 0, // Default to 0 for new form data
      deploymentId: null // Default to null for new form data
    };
  }

  // Set company GUID for IndexedDB operations
  setCompanyGuid(guid: string): void {
    // Ensure we're using just the company GUID, not the full encoded string
    this.companyGuid = this.extractCompanyGuid(guid);
    console.log('FormBuilderV2Service: Set company GUID:', this.companyGuid);
  }

  getCompanyGuid(): string {
    return this.companyGuid;
  }

  /**
   * Extract company GUID from any encoded ID format (legacy or new)
   * This ensures consistent GUID usage across the application
   */
  private extractCompanyGuid(userId: string): string {
    try {
      // If userId contains legacy format indicators, extract just the GUID
      if (userId.includes(':')) {
        const parts = userId.split(':');
        if (parts.length === 3 && parts[0] === 'otto-website-builder-2024') {
          // Legacy format: salt:guid:timestamp
          return parts[1]; // Return just the GUID
        }
      }
      
      // New format: just the GUID
      return userId;
    } catch (error) {
      console.error('FormBuilderV2Service: Failed to extract company GUID:', error);
      return userId; // Fallback to original userId
    }
  }

  // Form data management
  updateFormData(updates: Partial<FormBuilderV2Data>): void {
    const currentData = this.formDataSubject.value;
    const updatedData = { ...currentData, ...updates };
    this.formDataSubject.next(updatedData);
    this.saveToIndexedDB(updatedData);
  }

  getFormData(): FormBuilderV2Data {
    return this.formDataSubject.value;
  }

  resetFormData(): void {
    const initialData = this.getInitialFormData();
    this.formDataSubject.next(initialData);
    
    // Reset AI content generation state
    this.isAIContentGeneratedSubject.next(false);
    
    this.clearIndexedDB();
  }

  // IndexedDB storage management
  // NOTE: This service ONLY handles form data (supplier data interface) persistence
  // Tag replacements are handled separately using 'tagReplacements_${companyGuid}' key
  // This ensures proper separation of concerns between form data and AI-generated content
  private async saveToIndexedDB(data: FormBuilderV2Data): Promise<void> {
    try {
      // Store complete form data with company GUID
      // This key is specifically for form data (supplier data interface)
      const storageKey = this.companyGuid ? `v2_completeFormData_${this.companyGuid}` : 'v2_completeFormData';
      await this.indexedDBService.storeMetadata(storageKey, data);
      
      // Also store individual components for compatibility with V1 system
      if (this.companyGuid) {
        // Store template selection using V1 pattern
        await this.indexedDBService.storeMetadata(`selectedTemplate_${this.companyGuid}`, data.template);
        
        // Store theme selection using V1 pattern
        await this.indexedDBService.setUserTheme(this.companyGuid, data.theme);
        
        // Get the sample logo blob URL from WebsiteFileService
        let sampleLogoUrl = '';
        // try {
        //   // Get the blob URL for the sample logo (logo-placeholder.png) for the current template
        //   debugger;
        //   sampleLogoUrl = this.previewService.getWebsiteFileService().getBlobUrl('images/logo-placeholder.png') || '';
        //   console.log('✅ Sample logo blob URL retrieved:', sampleLogoUrl ? sampleLogoUrl.substring(0, 50) + '...' : 'Not found');
        // } catch (error) {
        //   console.warn('⚠️ Could not retrieve sample logo blob URL:', error);
        // }
        
        // Store supplier data for compatibility - PRESERVE existing company name
        const existingSupplierData = await this.indexedDBService.getUserSupplierData(this.companyGuid);
        const supplierData = {
          companyName: existingSupplierData?.companyName || '', // Preserve existing company name from DynamoDB
          contactEmail: data.contact?.email || '',
          phoneNumber: data.contact?.phone || '',
          streetAddress: data.contact?.streetAddress || '',
          city: data.contact?.city || '',
          countryRegion: data.contact?.state || '',
          postalCode: data.contact?.zipCode || '',
          aboutContent: data.aboutUs || '',
          capabilities: data.capabilities.join(', ') || '',
          equipment: data.equipment || '',
          websiteURL: '',
          streetAddress2: '',
          logoURL: '' // Logo URL is now handled through tag replacements
        };
        await this.indexedDBService.setUserSupplierData(this.companyGuid, supplierData);
        
        console.log('✅ Supplier data saved (logo URL handled through tag replacements)');
      }
      
      console.log('✅ V2 form data saved to IndexedDB');
    } catch (error) {
      console.error('Error saving form data to IndexedDB:', error);
    }
  }

  async loadFromIndexedDB(): Promise<void> {
    try {
      // Load complete form data with company GUID
      // This key is specifically for form data (supplier data interface)
      const storageKey = this.companyGuid ? `v2_completeFormData_${this.companyGuid}` : 'v2_completeFormData';
      const savedData = await this.indexedDBService.getMetadata(storageKey);
      if (savedData) {
        this.formDataSubject.next(savedData);
        
        // Restore AI content generation state from form data
        const aiContentGenerated = savedData.isAIContentGenerated || false;
        this.isAIContentGeneratedSubject.next(aiContentGenerated);
        
        console.log('✅ V2 form data loaded from IndexedDB');
        console.log('✅ AI content generation state restored:', aiContentGenerated);
        return;
      }

      // Fallback: Load individual components from V1 system
      if (this.companyGuid) {
        const template = await this.indexedDBService.getMetadata(`selectedTemplate_${this.companyGuid}`);
        const theme = await this.indexedDBService.getUserTheme(this.companyGuid);
        const supplierData = await this.indexedDBService.getUserSupplierData(this.companyGuid);
        
        // Load custom theme color from V1 user preferences
        const customThemeColor = await this.indexedDBService.getUserCustomThemeColor(this.companyGuid) || '#2e58d6';

        const restoredData = this.getInitialFormData();
        
        if (template) restoredData.template = template;
        if (theme) restoredData.theme = theme;
        // if (theme === 'custom') {
          restoredData.customThemeColor = customThemeColor;
        // }
        if (supplierData) {
          restoredData.contact.email = supplierData.contactEmail || '';
          restoredData.contact.phone = supplierData.phoneNumber || '';
          restoredData.contact.streetAddress = supplierData.streetAddress || '';
          restoredData.contact.city = supplierData.city || '';
          restoredData.contact.state = supplierData.countryRegion || '';
          restoredData.contact.zipCode = supplierData.postalCode || '';
          restoredData.aboutUs = supplierData.aboutContent || '';
          restoredData.equipment = supplierData.equipment || '';
          if (supplierData.capabilities) {
            restoredData.capabilities = supplierData.capabilities.split(', ').filter((c: string) => c.trim());
          }
          // Note: logoURL is now handled through tag replacements, not SupplierData interface
        }

        this.formDataSubject.next(restoredData);
        console.log('✅ V2 form data restored from V1 IndexedDB data');
      }
    } catch (error) {
      console.error('Error loading form data from IndexedDB:', error);
    }
  }

  private async clearIndexedDB(): Promise<void> {
    try {
      // Clear only form data - DO NOT clear tag replacements
      // Tag replacements are handled separately and should persist
      const storageKey = this.companyGuid ? `v2_completeFormData_${this.companyGuid}` : 'v2_completeFormData';
      await this.indexedDBService.deleteMetadata(storageKey);
      if (this.companyGuid) {
        await this.indexedDBService.deleteMetadata(`selectedTemplate_${this.companyGuid}`);
        // Note: We don't clear all user data here to preserve tag replacements
        // Only clear specific form-related data
        await this.indexedDBService.deleteMetadata(`userPreferences_${this.companyGuid}`);
      }
      console.log('✅ V2 form data cleared from IndexedDB (tag replacements preserved)');
    } catch (error) {
      console.error('Error clearing form data from IndexedDB:', error);
    }
  }

  /**
   * Restore tag replacements from IndexedDB using the correct key
   * This method ensures tag replacements are handled separately from form data
   * @returns Promise<TagReplacement[]> - Array of restored tag replacements
   */
  async restoreTagReplacements(): Promise<TagReplacement[]> {
    try {
      if (!this.companyGuid) {
        console.warn('FormBuilderV2Service: No company GUID set, cannot restore tag replacements');
        return [];
      }

      // Use the correct key for tag replacements
      const tagReplacements = await this.indexedDBService.getTagReplacements(this.companyGuid);
      
      if (tagReplacements && tagReplacements.length > 0) {
        console.log(`✅ V2: Restored ${tagReplacements.length} tag replacements from IndexedDB using key 'tagReplacements_${this.companyGuid}'`);
        return tagReplacements;
      } else {
        console.log('ℹ️ V2: No tag replacements found in IndexedDB');
        return [];
      }
    } catch (error) {
      console.error('Error restoring tag replacements from IndexedDB:', error);
      return [];
    }
  }

  /**
   * Clear tag replacements from IndexedDB using the correct key
   * This method ensures tag replacements are handled separately from form data
   */
  async clearTagReplacements(): Promise<void> {
    try {
      if (!this.companyGuid) {
        console.warn('FormBuilderV2Service: No company GUID set, cannot clear tag replacements');
        return;
      }

      // Use the correct key for tag replacements
      await this.indexedDBService.deleteMetadata(`tagReplacements_${this.companyGuid}`);
      console.log(`✅ V2: Cleared tag replacements from IndexedDB using key 'tagReplacements_${this.companyGuid}'`);
    } catch (error) {
      console.error('Error clearing tag replacements from IndexedDB:', error);
    }
  }

  /**
   * Debug method to show all currently stored tag replacements
   * This helps identify which tags are missing or present in IndexedDB
   */
  async debugTagReplacements(): Promise<void> {
    try {
      if (!this.companyGuid) {
        console.warn('FormBuilderV2Service: No company GUID set, cannot debug tag replacements');
        return;
      }

      const tagReplacements = await this.indexedDBService.getTagReplacements(this.companyGuid);
      
      if (tagReplacements && tagReplacements.length > 0) {
        console.log(`🔍 V2: Found ${tagReplacements.length} tag replacements in IndexedDB:`);
        
        // Group tags by category for better readability
        const equipmentTags = tagReplacements.filter(t => t.tagName.startsWith('EQUIPMENT_'));
        const serviceTags = tagReplacements.filter(t => t.tagName.startsWith('SERVICE_'));
        const aboutTags = tagReplacements.filter(t => t.tagName.startsWith('ABOUT_'));
        const capabilitiesTags = tagReplacements.filter(t => t.tagName.startsWith('CAPABILITIES_'));
        const heroTags = tagReplacements.filter(t => t.tagName.startsWith('HERO_'));
        const additionalTags = tagReplacements.filter(t => t.tagName.startsWith('ADDITIONAL_'));
        const otherTags = tagReplacements.filter(t => 
          !t.tagName.startsWith('EQUIPMENT_') && 
          !t.tagName.startsWith('SERVICE_') && 
          !t.tagName.startsWith('ABOUT_') && 
          !t.tagName.startsWith('CAPABILITIES_') && 
          !t.tagName.startsWith('HERO_') && 
          !t.tagName.startsWith('ADDITIONAL_')
        );

        if (equipmentTags.length > 0) {
          console.log(`  🏭 Equipment tags (${equipmentTags.length}): ${equipmentTags.map(t => t.tagName).join(', ')}`);
        }
        if (serviceTags.length > 0) {
          console.log(`  🔧 Service tags (${serviceTags.length}): ${serviceTags.map(t => t.tagName).join(', ')}`);
        }
        if (aboutTags.length > 0) {
          console.log(`  ℹ️ About tags (${aboutTags.length}): ${aboutTags.map(t => t.tagName).join(', ')}`);
        }
        if (capabilitiesTags.length > 0) {
          console.log(`  ⚡ Capabilities tags (${capabilitiesTags.length}): ${capabilitiesTags.map(t => t.tagName).join(', ')}`);
        }
        if (heroTags.length > 0) {
          console.log(`  🎯 Hero tags (${heroTags.length}): ${heroTags.map(t => t.tagName).join(', ')}`);
        }
        if (additionalTags.length > 0) {
          console.log(`  ➕ Additional tags (${additionalTags.length}): ${additionalTags.map(t => t.tagName).join(', ')}`);
        }
        if (otherTags.length > 0) {
          console.log(`  📝 Other tags (${otherTags.length}): ${otherTags.map(t => t.tagName).join(', ')}`);
        }
      } else {
        console.log('ℹ️ V2: No tag replacements found in IndexedDB');
      }
    } catch (error) {
      console.error('Error debugging tag replacements:', error);
    }
  }

  /**
   * Validate that all expected equipment tags are present for the current template
   * This helps identify missing tags that should have been generated
   */
  async validateEquipmentTags(): Promise<void> {
    try {
      if (!this.companyGuid) {
        console.warn('FormBuilderV2Service: No company GUID set, cannot validate equipment tags');
        return;
      }

      const tagReplacements = await this.indexedDBService.getTagReplacements(this.companyGuid);
      const currentFormData = this.getFormData();
      const templateNumber = currentFormData.template;
      
      // Define expected equipment tags for each template
      const expectedEquipmentTags = [
        'EQUIPMENT_1_TITLE', 'EQUIPMENT_1_DESCRIPTION',
        'EQUIPMENT_2_TITLE', 'EQUIPMENT_2_DESCRIPTION',
        'EQUIPMENT_3_TITLE', 'EQUIPMENT_3_DESCRIPTION',
        'EQUIPMENT_4_TITLE', 'EQUIPMENT_4_DESCRIPTION',
        'EQUIPMENT_5_TITLE', 'EQUIPMENT_5_DESCRIPTION',
        'EQUIPMENT_6_TITLE', 'EQUIPMENT_6_DESCRIPTION'
      ];

      if (tagReplacements && tagReplacements.length > 0) {
        const presentEquipmentTags = tagReplacements
          .filter(t => t.tagName.startsWith('EQUIPMENT_'))
          .map(t => t.tagName);
        
        const missingEquipmentTags = expectedEquipmentTags.filter(tag => 
          !presentEquipmentTags.includes(tag)
        );
        
        console.log(`🔍 V2: Equipment tag validation for template ${templateNumber}:`);
        console.log(`  ✅ Present equipment tags (${presentEquipmentTags.length}): ${presentEquipmentTags.join(', ')}`);
        
        if (missingEquipmentTags.length > 0) {
          console.log(`  ❌ Missing equipment tags (${missingEquipmentTags.length}): ${missingEquipmentTags.join(', ')}`);
          console.log(`  ⚠️ This indicates that some equipment content was not properly saved to IndexedDB`);
        } else {
          console.log(`  ✅ All expected equipment tags are present`);
        }
      } else {
        console.log(`❌ V2: No tag replacements found - all equipment tags are missing`);
      }
    } catch (error) {
      console.error('Error validating equipment tags:', error);
    }
  }


  // Home section methods
  updateHomeData(homeData: any): void {
    this.formDataSubject.next({
      ...this.formDataSubject.value,
      home: homeData
    });
    this.saveToIndexedDB({ ...this.formDataSubject.value, home: homeData });
  }

  updateHeroTitle(title: string): void {
    const currentData = this.formDataSubject.value;
    this.formDataSubject.next({
      ...currentData,
      home: {
        ...currentData.home,
        heroTitle: title
      }
    });
    this.saveToIndexedDB({ ...this.formDataSubject.value, home: { ...currentData.home, heroTitle: title } });
  }

  updateHeroSubtitle(subtitle: string): void {
    const currentData = this.formDataSubject.value;
    this.formDataSubject.next({
      ...currentData,
      home: {
        ...currentData.home,
        heroSubtitle: subtitle
      }
    });
    this.saveToIndexedDB({ ...this.formDataSubject.value, home: { ...currentData.home, heroSubtitle: subtitle } });
  }

  updateHeroDescription(description: string): void {
    const currentData = this.formDataSubject.value;
    this.formDataSubject.next({
      ...currentData,
      home: {
        ...currentData.home,
        heroDescription: description
      }
    });
    this.saveToIndexedDB({ ...this.formDataSubject.value, home: { ...currentData.home, heroDescription: description } });
  }

  // Template/Theme methods
  async updateTemplate(templateId: number): Promise<void> {
    this.updateFormData({ template: templateId });
    
    // Update V1-compatible stream
    this.selectedTemplateSubject.next(templateId);
    
    // Store in IndexedDB using V1 pattern
    await this.indexedDBService.storeMetadata('selectedTemplate', templateId);
    
    // Save complete form data to IndexedDB to ensure logo URL is set with sample logo
    const currentData = this.formDataSubject.value;
    await this.saveToIndexedDB(currentData);
    
    console.log('✅ Template updated and stored in IndexedDB:', templateId);
  }

  async updateTheme(theme: string): Promise<void> {
    this.updateFormData({ theme });
    
    // Update V1-compatible stream
    this.selectedColorThemeSubject.next(theme);
    
    // Store in IndexedDB using V1 pattern
    if (this.companyGuid) {
      await this.indexedDBService.setUserTheme(this.companyGuid, theme);
      console.log('✅ Theme updated and stored in IndexedDB:', theme);
    }
  }

  async updateCustomThemeColor(color: string): Promise<void> {
    console.log('FormBuilderV2Service: Updating custom theme color to:', color);
    
    const currentData = this.formDataSubject.value;
    const updatedData = { ...currentData, customThemeColor: color };
    this.formDataSubject.next(updatedData);
    
    // Save to IndexedDB (V2 storage)
    await this.saveToIndexedDB(updatedData);
    
    // Also save to V1 user preferences system for backward compatibility
    if (this.companyGuid) {
      await this.indexedDBService.setUserCustomThemeColor(this.companyGuid, color);
      console.log('FormBuilderV2Service: Custom theme color also saved to V1 user preferences');
    }
    
    console.log('FormBuilderV2Service: Custom theme color updated successfully');
  }

  // Capabilities management
  updateCapabilities(capabilities: string[]): void {
    this.updateFormData({ capabilities });
  }

  updateAdditionalCapabilities(additionalCapabilities: string): void {
    this.updateFormData({ additionalCapabilities });
  }

  updateEquipment(equipment: string): void {
    this.updateFormData({ equipment });
  }

  // About Us management
  updateAboutUs(aboutUs: string): void {
    this.updateFormData({ aboutUs });
  }

  // Contact management
  updateContact(contact: FormBuilderV2Data['contact']): void {
    this.updateFormData({ contact });
  }

  // Logo management
  updateLogo(logo: File): void {
    this.updateFormData({ logo });
  }

  // Image management
  addImage(section: 'aboutUs' | 'capabilities', file: File): void {
    const currentData = this.formDataSubject.value;
    const updatedImages = {
      ...currentData.images,
      [section]: [...currentData.images[section], file]
    };
    this.updateFormData({ images: updatedImages });
  }

  removeImage(section: 'aboutUs' | 'capabilities', index: number): void {
    const currentData = this.formDataSubject.value;
    const updatedImages = {
      ...currentData.images,
      [section]: currentData.images[section].filter((_, i) => i !== index)
    };
    this.updateFormData({ images: updatedImages });
  }

  // Validation
  validateFormData(): { isValid: boolean; errors: string[] } {
    const data = this.formDataSubject.value;
    const errors: string[] = [];

    // Basic validation
    if (!data.template) {
      errors.push('Please select a template');
    }

    if (!data.theme) {
      errors.push('Please select a theme color');
    }

    if (data.capabilities.length === 0) {
      errors.push('Please select capabilities');
    }

    if (!data.equipment.trim()) {
      errors.push('Please provide equipment information');
    }

    if (!data.aboutUs.trim()) {
      errors.push('Please provide company description');
    }

    // Contact validation
    if (!data.contact.email.trim()) {
      errors.push('Please provide email address');
    }

    if (!data.contact.phone.trim()) {
      errors.push('Please provide phone number');
    }

    if (!data.contact.streetAddress.trim()) {
      errors.push('Please provide street address');
    }

    if (!data.contact.city.trim()) {
      errors.push('Please provide city');
    }

    if (!data.contact.state.trim()) {
      errors.push('Please provide state');
    }

    if (!data.contact.zipCode.trim()) {
      errors.push('Please provide zip code');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // Save for later functionality
  saveForLater(): Observable<any> {
    // This would typically save to a backend service
    return this.http.post(`${this.apiUrl}/save-form-data`, this.getFormData());
  }

  // Publish site functionality
  publishSite(): Observable<any> {
    // This would typically trigger the website generation and deployment
    return this.http.post(`${this.apiUrl}/publish-site`, this.getFormData());
  }

  // Current Active Section management
  async updateCurrentActiveSection(section: string | null): Promise<void> {
    console.log('FormBuilderV2Service: Updating current active section to:', section);
    const currentData = this.formDataSubject.value;
    const updatedData = { ...currentData, currentActiveSection: section };
    this.formDataSubject.next(updatedData);
    await this.saveToIndexedDB(updatedData);
  }

  getCurrentActiveSection(): string | null {
    return this.formDataSubject.value.currentActiveSection || null;
  }

  // Section Completion States management
  async updateSectionCompletion(section: keyof SectionCompletionStates, isCompleted: boolean): Promise<void> {
    console.log(`FormBuilderV2Service: Updating ${section} completion to:`, isCompleted);
    const currentData = this.formDataSubject.value;
    const currentCompletionStates = currentData.sectionCompletionStates || this.getInitialFormData().sectionCompletionStates!;
    
    const updatedCompletionStates = {
      ...currentCompletionStates,
      [section]: isCompleted
    };
    
    const updatedData = {
      ...currentData,
      sectionCompletionStates: updatedCompletionStates
    };
    
    this.formDataSubject.next(updatedData);
    await this.saveToIndexedDB(updatedData);
  }

  async updateAllSectionCompletions(completionStates: SectionCompletionStates): Promise<void> {
    console.log('FormBuilderV2Service: Updating all section completion states:', completionStates);
    const currentData = this.formDataSubject.value;
    const updatedData = {
      ...currentData,
      sectionCompletionStates: completionStates
    };
    
    this.formDataSubject.next(updatedData);
    await this.saveToIndexedDB(updatedData);
  }

  getSectionCompletion(section: keyof SectionCompletionStates): boolean {
    const completionStates = this.formDataSubject.value.sectionCompletionStates;
    return completionStates ? completionStates[section] : false;
  }

  getAllSectionCompletions(): SectionCompletionStates {
    return this.formDataSubject.value.sectionCompletionStates || this.getInitialFormData().sectionCompletionStates!;
  }

  // Reset section completions when template changes
  async resetSectionCompletionsOnTemplateChange(): Promise<void> {
    console.log('FormBuilderV2Service: Resetting section completions due to template change');
    
    const currentData = this.formDataSubject.value;
    const initialCompletionStates = this.getInitialFormData().sectionCompletionStates!;
    
    const updatedData = {
      ...currentData,
      sectionCompletionStates: initialCompletionStates,
      completionPercentage: 0
    };
    
    this.formDataSubject.next(updatedData);
    await this.saveToIndexedDB(updatedData);
    
    console.log('✅ FormBuilderV2Service: Section completions reset to initial state');
  }

  // Get generation states from form data
  getGenerationStates(): { isAboutGenerated: boolean; isCapabilitiesGenerated: boolean } {
    const formData = this.formDataSubject.value;
    return {
      isAboutGenerated: formData.isAboutGenerated || false,
      isCapabilitiesGenerated: formData.isCapabilitiesGenerated || false
    };
  }

  // Completion Percentage management
  async updateCompletionPercentage(completionPercentage: number): Promise<void> {
    const currentData = this.formDataSubject.value;
    const updatedData = { ...currentData, completionPercentage };
    this.formDataSubject.next(updatedData);
    await this.saveToIndexedDB(updatedData);
  }

  getCompletionPercentage(): number {
    return this.formDataSubject.value.completionPercentage || 0;
  }

  // Deployment ID management
  async updateDeploymentId(deploymentId: string): Promise<void> {
    console.log('FormBuilderV2Service: Updating deployment ID to:', deploymentId);
    const currentData = this.formDataSubject.value;
    const updatedData = { ...currentData, deploymentId };
    this.formDataSubject.next(updatedData);
    await this.saveToIndexedDB(updatedData);
  }

  getDeploymentId(): string | null {
    return this.formDataSubject.value.deploymentId || null;
  }
}